import React from "react";
import { Link, useLocation } from "react-router-dom";
import { CaretRight, House } from "@phosphor-icons/react";

// Map path segments to friendly names + parent module labels
const PATH_MAP = {
  "": { label: "Home", module: null },
  projects: { label: "Projects", module: "Projects" },
  invoices: { label: "Invoices", module: "Sales & Receivables" },
  recurring: { label: "Recurring", module: "Sales & Receivables" },
  payments: { label: "Payments", module: "Sales & Receivables" },
  aging: { label: "Aging Report", module: "Sales & Receivables" },
  sales: { label: "Sales", module: "Sales & Receivables" },
  "vendor-pos": { label: "Vendor POs", module: "Procurement & Inventory" },
  purchases: { label: "Purchases", module: "Procurement & Inventory" },
  expenses: { label: "Expenses", module: "Procurement & Inventory" },
  inventory: { label: "Site Inventory", module: "Procurement & Inventory" },
  warehouses: { label: "Warehouses", module: "Procurement & Inventory" },
  "goods-receipts": { label: "Goods Receipts", module: "Procurement & Inventory" },
  "stock-transfers": { label: "Stock Transfers", module: "Procurement & Inventory" },
  profit: { label: "Profit & Loss", module: "Finance & Reports" },
  finance: { label: "Finance", module: "Finance & Reports" },
  meetings: { label: "Meetings / MOM", module: "Projects" },
  service: { label: "Service & Support", module: "Service & Support" },
  users: { label: "Team", module: "Administration" },
  settings: { label: "Company", module: "Administration" },
  security: { label: "Security", module: "Administration" },
  approvals: { label: "Approvals", module: "Administration" },
};

// Sub-path labels for the second segment (e.g. /service/equipment)
const SUBPATH_LABELS = {
  equipment: "Equipment Registry",
  snags: "Snag Lists",
  tickets: "Service Tickets",
  commissioning: "Commissioning Reports",
  ppm: "Preventive Maintenance",
  warranty: "Warranty Tracker",
  csat: "Client Feedback (CSAT)",
  audit: "Audit Log",
  permissions: "Permission Matrix",
  "2fa": "Two-Factor Auth",
  "balance-sheet": "Balance Sheet",
  "budgets": "Budgets",
  "opening-balances": "Opening Balances",
};

function Breadcrumbs() {
  const location = useLocation();
  const parts = location.pathname.split("/").filter(Boolean);
  const first = parts[0] || "";
  const meta = PATH_MAP[first] || { label: first, module: null };

  return (
    <nav
      className="flex items-center gap-1 text-[11px] text-[#605E5C]"
      aria-label="breadcrumb"
      data-testid="breadcrumbs"
    >
      <Link to="/" className="flex items-center gap-1 hover:text-[#2E75B6]">
        <House size={11} weight="fill" />
        <span>Home</span>
      </Link>
      {meta.module && (
        <>
          <CaretRight size={9} className="text-[#A19F9D]" />
          <span>{meta.module}</span>
        </>
      )}
      {first && (
        <>
          <CaretRight size={9} className="text-[#A19F9D]" />
          <span className="text-[#201F1E] font-semibold">{meta.label}</span>
        </>
      )}
      {parts.length > 1 && (
        <>
          <CaretRight size={9} className="text-[#A19F9D]" />
          <span className="text-[#323130] font-semibold">
            {SUBPATH_LABELS[parts[1]] || parts.slice(1).join(" / ")}
          </span>
        </>
      )}
    </nav>
  );
}

/**
 * BC-style PageHeader: two rows.
 *   Row 1 — dense breadcrumb + tenant chip.
 *   Row 2 — title (18-19px) + optional inline actions.
 *   Row 3 (optional) — Action Ribbon: gray strip with icon-over-text buttons.
 */
export function PageHeader({ title, subtitle, actions, ribbon, testid, module }) {
  return (
    <div className="bg-white border-b border-[#EDEBE9]" data-testid={testid}>
      <div className="px-6 pt-3 pb-2">
        <Breadcrumbs />
        <div className="flex items-end justify-between gap-4 mt-2">
          <div className="min-w-0">
            <div className="flex items-baseline gap-3 flex-wrap">
              {module && <span className="section-eyebrow">{module}</span>}
            </div>
            <h1 className="font-display text-[19px] md:text-[22px] font-semibold tracking-[-0.01em] text-[#201F1E] truncate leading-tight">
              {title}
            </h1>
            {subtitle && <p className="text-[12px] text-[#605E5C] mt-0.5 max-w-3xl">{subtitle}</p>}
          </div>
          {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
        </div>
      </div>
      {ribbon && <div className="bc-ribbon" data-testid="page-ribbon">{ribbon}</div>}
    </div>
  );
}

/**
 * BcRibbonBtn — icon over text button used inside a `.bc-ribbon` strip.
 * `variant` = default | primary | danger
 */
export function BcRibbonBtn({ icon: Icon, label, onClick, disabled, variant = "default", testid, title }) {
  const cls = variant === "primary" ? "bc-ribbon-btn primary" : variant === "danger" ? "bc-ribbon-btn danger" : "bc-ribbon-btn";
  return (
    <button
      type="button"
      className={cls}
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      title={title || label}
    >
      {Icon && <Icon size={17} weight={variant === "primary" ? "fill" : "regular"} />}
      <span className="lbl">{label}</span>
    </button>
  );
}

/**
 * BcRibbonGroup — a labelled cluster of `.bc-ribbon-btn`s.
 * Displays a small caption underneath (mimics BC's ribbon group titles like "Manage", "Process").
 */
export function BcRibbonGroup({ label, children }) {
  return (
    <div className="bc-ribbon-group flex flex-col">
      <div className="flex items-stretch">{children}</div>
      {label && (
        <div className="text-[9.5px] text-[#605E5C] uppercase tracking-[0.06em] text-center mt-0.5 select-none">
          {label}
        </div>
      )}
    </div>
  );
}

/**
 * BcRibbon — flat action bar with grouped BcRibbonBtn columns.
 * Usage:
 *   <BcRibbon>
 *     <BcRibbonGroup label="New">…buttons…</BcRibbonGroup>
 *     <BcRibbonGroup label="Manage">…buttons…</BcRibbonGroup>
 *   </BcRibbon>
 */
export function BcRibbon({ children }) {
  return <>{children}</>;
}

const STATUS_MAP = {
  draft:       { bg: "bg-[#F3F2F1]", text: "text-[#605E5C]", dot: "bg-[#8A8886]", label: "Draft" },
  sent:        { bg: "bg-[#DEECF9]", text: "text-[#2E75B6]", dot: "bg-[#2E75B6]", label: "Sent" },
  partial:     { bg: "bg-[#FFF4CE]", text: "text-[#8A6E00]", dot: "bg-[#8A6E00]", label: "Partial" },
  paid:        { bg: "bg-[#DFF6DD]", text: "text-[#107C10]", dot: "bg-[#107C10]", label: "Paid" },
  overdue:     { bg: "bg-[#FDE7E9]", text: "text-[#A4262C]", dot: "bg-[#A4262C]", label: "Overdue" },
  planning:    { bg: "bg-[#F3F2F1]", text: "text-[#605E5C]", dot: "bg-[#8A8886]", label: "Planning" },
  in_progress: { bg: "bg-[#DEECF9]", text: "text-[#2E75B6]", dot: "bg-[#2E75B6]", label: "In Progress" },
  on_hold:     { bg: "bg-[#FFF4CE]", text: "text-[#8A6E00]", dot: "bg-[#8A6E00]", label: "On Hold" },
  completed:   { bg: "bg-[#DFF6DD]", text: "text-[#107C10]", dot: "bg-[#107C10]", label: "Completed" },
  pending_approval: { bg: "bg-[#FFF4CE]", text: "text-[#8A6E00]", dot: "bg-[#8A6E00]", label: "Pending" },
  approved:    { bg: "bg-[#DFF6DD]", text: "text-[#107C10]", dot: "bg-[#107C10]", label: "Approved" },
  rejected:    { bg: "bg-[#FDE7E9]", text: "text-[#A4262C]", dot: "bg-[#A4262C]", label: "Rejected" },
};

export function StatusBadge({ status }) {
  const s = STATUS_MAP[status] || { bg: "bg-[#F3F2F1]", text: "text-[#605E5C]", dot: "bg-[#8A8886]", label: status };
  return (
    <span className={`inline-flex items-center gap-1.5 px-1.5 py-[1px] text-[10.5px] font-semibold ${s.bg} ${s.text} rounded-[2px]`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

export function EmptyState({ title, description, action }) {
  return (
    <div className="border border-dashed border-[#C8C6C4] bg-white p-12 text-center rounded-[2px]" data-testid="empty-state">
      <div className="font-display text-[15px] font-semibold text-[#201F1E]">{title}</div>
      {description && <div className="text-[12.5px] text-[#605E5C] mt-1.5 max-w-md mx-auto leading-relaxed">{description}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}
